import { useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

import { injectTourTheme } from '../config/tourTheme';
import tourDefaults from '../config/tourDefaults';
import TOUR_REGISTRY from '../config/tourRegistry';
import { completeTour, setActiveTour } from '../redux/onboardingActions';
import { waitForElement, getRegionContent } from '../utils/tourUtils';
import {
  trackTourStarted,
  trackTourStepViewed,
  trackTourCompleted,
  trackTourAbandoned,
} from '../analytics/onboardingEvents';
import { store } from '../../../redux/store';

export default function useTour() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const themeMode = useSelector((state) => state.theme?.mode || 'light');
  const countryPref = useSelector((state) => state.user?.countryPreference);
  const tourRef = useRef(null);
  const startTimeRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.cancel();
        tourRef.current = null;
      }
    };
  }, []);

  const startTour = useCallback(
    async (tourId) => {
      const entry = TOUR_REGISTRY[tourId];
      if (!entry) return;

      // Inject theme CSS
      injectTourTheme(themeMode);

      // Navigate if needed
      if (location.pathname !== entry.route) {
        navigate(entry.route);
        // Wait for route render
        await new Promise((r) => setTimeout(r, 800));
      }

      const regionContent = getRegionContent(countryPref);
      const steps = entry.getSteps(t, { navigate, dispatch, store, regionContent });

      // Create tour instance
      const tour = new Shepherd.Tour(tourDefaults);

      // Wire up button actions — Shepherd expects function refs, not strings
      steps.forEach((step) => {
        if (step.buttons) {
          step.buttons = step.buttons.map((btn) => {
            if (btn.action === 'next') return { ...btn, action: () => tour.next() };
            if (btn.action === 'back') return { ...btn, action: () => tour.back() };
            if (btn.action === 'complete') return { ...btn, action: () => tour.complete() };
            if (btn.action === 'cancel') return { ...btn, action: () => tour.cancel() };
            return btn;
          });
        }
        tour.addStep(step);
      });

      // Analytics: tour started
      startTimeRef.current = Date.now();
      let currentStepIndex = 0;

      tour.on('show', () => {
        const step = tour.getCurrentStep();
        currentStepIndex = tour.steps.indexOf(step);
        trackTourStepViewed(tourId, currentStepIndex, step?.id);
      });

      tour.on('complete', () => {
        const duration = Date.now() - (startTimeRef.current || Date.now());
        trackTourCompleted(tourId, steps.length, duration);
        dispatch(completeTour(tourId));
        dispatch(setActiveTour(null));
        navigate('/getting-started');
      });

      tour.on('cancel', () => {
        const duration = Date.now() - (startTimeRef.current || Date.now());
        trackTourAbandoned(tourId, currentStepIndex, steps.length, duration);
        dispatch(setActiveTour(null));
        navigate('/getting-started');
      });

      // Wait for first step target
      const firstTarget = steps[0]?.attachTo?.element;
      if (firstTarget) {
        try {
          await waitForElement(firstTarget, 5000);
        } catch {
          // Target not found — start anyway (Shepherd handles missing targets)
        }
      }

      trackTourStarted(tourId, steps.length);
      dispatch(setActiveTour(tourId));
      tourRef.current = tour;
      tour.start();
    },
    [themeMode, countryPref, location.pathname, navigate, dispatch, t]
  );

  const cancelTour = useCallback(() => {
    if (tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }
  }, []);

  return { startTour, cancelTour };
}
